import { FC } from 'react';
import { Save } from 'lucide-react';
import { cn } from '../../../../utils/cn';

interface ClientNotesEditorProps {
  clientId: string;
  notes: string;
  originalNotes: string;
  onNotesChange: (notes: string) => void;
  onSaveNotes: () => void;
  isSaving: boolean;
}

export const ClientNotesEditor: FC<ClientNotesEditorProps> = ({
  clientId,
  notes,
  originalNotes,
  onNotesChange,
  onSaveNotes,
  isSaving,
}) => {
  const hasNotesChanged = notes !== originalNotes;

  return (
    <div>
      <label
        htmlFor={`notes-${clientId}`}
        className="block text-xs font-medium text-gray-500 dark:text-slate-400 mb-2 uppercase tracking-wide"
      >
        Notes
      </label>
      <div className="space-y-2">
        <textarea
          id={`notes-${clientId}`}
          value={notes}
          onChange={(e) => onNotesChange(e.target.value)}
          placeholder="Add notes about this client's preferences, medical history, or other important details..."
          rows={3}
          className={cn(
            'w-full px-3 py-2 text-sm rounded-md border resize-none',
            'bg-white dark:bg-slate-700',
            'text-gray-900 dark:text-slate-100',
            'placeholder-gray-400 dark:placeholder-slate-500',
            'border-gray-300 dark:border-slate-600',
            'focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500'
          )}
        />
        {hasNotesChanged && (
          <div className="flex justify-end">
            <button
              type="button"
              onClick={onSaveNotes}
              disabled={isSaving}
              className={cn(
                'flex items-center gap-2 px-4 py-2 text-sm font-medium rounded-md',
                'bg-blue-600 text-white',
                'hover:bg-blue-700',
                'transition-colors',
                'disabled:opacity-50 disabled:cursor-not-allowed'
              )}
            >
              {isSaving ? (
                <>
                  <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent" />
                  <span>Saving...</span>
                </>
              ) : (
                <>
                  <Save className="w-4 h-4" />
                  <span>Save Notes</span>
                </>
              )}
            </button>
          </div>
        )}
      </div>
    </div>
  );
};
